import React, { useMemo } from 'react';
import { Image, StyleSheet, Text, View } from 'react-native';
import Svg, { Defs, LinearGradient, Rect, Stop } from 'react-native-svg';

import { ChipPng } from '../../../assets';
import { useSvgId } from '../../../utils/hooks/useSvgId';
import GreenButton from '../Event/GreenButton';
import {
	BONUS_FONT,
	BONUS_FONT_CONDENSED,
	bonusColors,
	bonusGlow,
	CARD_FILL,
	COMPACT_CARD_FILL,
	RADIUS_MD,
} from './bonusesTheme';
import { ArcadeGame } from './bonusGamesCatalog';

export type ArcadeCardVariant = 'featured' | 'compact';

/**
 * The two card masters as "16/10 VIP / bonuses / arcade games" (7019:79805) uses them. Both are
 * auto-layout columns in the file; the positions below are where that layout resolves, in the
 * 390-frame design space, so the card scales as a whole.
 *
 * The featured card sets its title in condensed SemiBold 16 over the deep `#770700` ramp and shows
 * the game's round medallion; the compact one sets condensed Black 12 capitals over the shallower
 * `#571405` and a square tile with a hairline rim.
 */
const SPECS = {
	// "Arcade / Game card" (3423:1266) - the 3-across featured row (7019:79900).
	featured: {
		width: 118,
		height: 176,
		fill: CARD_FILL,
		title: {
			x: 8,
			y: 8,
			width: 102,
			fontSize: 16,
			font: BONUS_FONT_CONDENSED,
			weight: '600' as const,
			tracking: 0,
			lines: 1,
		},
		artwork: { x: 8, y: 32, size: 102, rim: 0, fit: 'contain' as const },
		play: { x: 8, y: 138, width: 102, height: 28, fontSize: 14 },
	},
	// "Arcade / Game card / Compact / Four columns" (3445:4814) - the VIP grid (7019:79913).
	compact: {
		width: 84,
		height: 147,
		fill: COMPACT_CARD_FILL,
		title: {
			x: 6,
			y: 4,
			width: 72,
			fontSize: 12,
			font: BONUS_FONT_CONDENSED,
			weight: '900' as const,
			tracking: 0.48,
			lines: 2,
		},
		artwork: { x: 6, y: 35, size: 72, rim: 0.5, fit: 'cover' as const },
		play: { x: 6, y: 112, width: 72, height: 24, fontSize: 12 },
	},
} as const;

/** "Ribbon / Coming soon": 16 high across the tile's foot, gold/150 to gold/300. */
const RIBBON = { height: 16, fontSize: 10, tracking: 0.4 } as const;
const RIBBON_FILL: Array<[string, string]> = [
	['0', bonusColors.gold150],
	['1', bonusColors.gold300],
];

export const getArcadeCardSize = (variant: ArcadeCardVariant) => ({
	width: SPECS[variant].width,
	height: SPECS[variant].height,
});

type Props = {
	game: ArcadeGame;
	variant: ArcadeCardVariant;
	scale: number;
	/** Ignored on a coming-soon card. The VIP grid's goes through its VIP / ticket gate. */
	onPress?: () => void;
	playLabel?: string;
	/** Time left until the free play comes back, banded across the foot of the art. */
	timer?: string;
	/** Chips a paid play costs: the button shows the chip and this instead of `playLabel`. */
	price?: number;
};

export const ArcadeGameCard = ({
	game,
	variant,
	scale,
	onPress,
	playLabel = 'PLAY',
	timer,
	price,
}: Props) => {
	const spec = SPECS[variant];
	const styles = useMemo(() => createStyles(spec, scale), [spec, scale]);
	const fillId = useSvgId('arcadeCard');
	const ribbonId = useSvgId('arcadeRibbon');

	return (
		<View style={styles.card}>
			<View pointerEvents='none' style={StyleSheet.absoluteFill}>
				<Svg width='100%' height='100%'>
					<Defs>
						<LinearGradient id={fillId} x1='0' y1='0' x2='0' y2='1'>
							{spec.fill.map(([offset, color]) => (
								<Stop key={offset} offset={offset} stopColor={color} />
							))}
						</LinearGradient>
					</Defs>
					<Rect x='0' y='0' width='100%' height='100%' fill={`url(#${fillId})`} />
				</Svg>
			</View>

			{/* The file sets titles in Sofia Sans Condensed and the app bundles Semi Condensed, a
			    wider face. Worse, both bundled faces are variable fonts: the web build applies the
			    wght axis, Android cannot and fake-bolds instead, which is wider again - enough for
			    "RED OR BLACK" to ellipsize inside the design's 102 box on device. Shrinking to fit
			    keeps the design's size wherever it fits and only gives ground where it does not.
			    Each line of a two-line title is its own one-line Text: Android's shrink-to-fit
			    measured "HIGHER\nLOWER" as one line too wide and took it down to a few pixels. */}
			<View style={styles.titleBox}>
				{game.title
					.split('\n')
					.slice(0, spec.title.lines)
					.map(line => (
						<Text key={line} adjustsFontSizeToFit numberOfLines={1} style={styles.title}>
							{line}
						</Text>
					))}
			</View>

			<View style={styles.artwork}>
				<Image source={game.artwork} style={styles.artworkImage} resizeMode={spec.artwork.fit} />
				{game.comingSoon ? (
					<View style={styles.ribbon}>
						<View pointerEvents='none' style={StyleSheet.absoluteFill}>
							<Svg width='100%' height='100%'>
								<Defs>
									<LinearGradient id={ribbonId} x1='0' y1='0' x2='0' y2='1'>
										{RIBBON_FILL.map(([offset, color]) => (
											<Stop key={offset} offset={offset} stopColor={color} />
										))}
									</LinearGradient>
								</Defs>
								<Rect x='0' y='0' width='100%' height='100%' fill={`url(#${ribbonId})`} />
							</Svg>
						</View>
						<Text numberOfLines={1} style={styles.ribbonLabel}>
							COMING SOON
						</Text>
					</View>
				) : null}
				{timer && !game.comingSoon ? (
					<View pointerEvents='none' style={styles.timerBand}>
						<Text numberOfLines={1} style={styles.timerLabel}>
							{timer}
						</Text>
					</View>
				) : null}
				{spec.artwork.rim ? <View pointerEvents='none' style={styles.artworkRim} /> : null}
			</View>

			{/* Every live card in the design carries a green PLAY. The coming-soon cards draw the
			    grey one and have nothing to call - `disabled` would grey the fill differently. */}
			<GreenButton
				label={price !== undefined ? price.toLocaleString('en-US') : playLabel}
				icon={price !== undefined ? ChipPng : undefined}
				onPress={game.comingSoon ? undefined : onPress}
				tone={game.comingSoon ? 'grey' : 'green'}
				width={spec.play.width * scale}
				height={spec.play.height * scale}
				fontSize={spec.play.fontSize * scale}
				radius={RADIUS_MD * scale}
				borderWidth={Math.max(1, 1 * scale)}
				style={styles.play}
			/>
		</View>
	);
};

const createStyles = (spec: (typeof SPECS)[ArcadeCardVariant], scale: number) =>
	StyleSheet.create({
		card: {
			width: spec.width * scale,
			height: spec.height * scale,
			borderRadius: RADIUS_MD * scale,
			borderWidth: Math.max(1, 1 * scale),
			borderColor: bonusColors.cardBorder,
			overflow: 'hidden',
			...bonusGlow.card,
		},
		// The design gives the two-line compact titles a 26-high box and centres them in it, so a
		// one-line "PLINKO" sits level with the middle of "HIGHER LOWER".
		titleBox: {
			position: 'absolute',
			left: spec.title.x * scale,
			top: spec.title.y * scale,
			width: spec.title.width * scale,
			height: (spec.title.lines === 2 ? 26 : 20) * scale,
			justifyContent: 'center',
		},
		title: {
			color: bonusColors.accent,
			fontFamily: spec.title.font,
			fontSize: spec.title.fontSize * scale,
			fontWeight: spec.title.weight,
			letterSpacing: spec.title.tracking * scale,
			lineHeight: spec.title.fontSize * 1.2 * scale,
			textAlign: 'center',
		},
		artwork: {
			position: 'absolute',
			left: spec.artwork.x * scale,
			top: spec.artwork.y * scale,
			width: spec.artwork.size * scale,
			height: spec.artwork.size * scale,
			borderRadius: RADIUS_MD * scale,
			overflow: 'hidden',
		},
		artworkImage: {
			width: '100%',
			height: '100%',
		},
		// The tiles' 0.5 gold hairline, drawn over the art so the ribbon tucks under it too.
		artworkRim: {
			...StyleSheet.absoluteFillObject,
			borderRadius: RADIUS_MD * scale,
			borderWidth: Math.max(0.5, spec.artwork.rim * scale),
			borderColor: '#FCF17B',
		},
		ribbon: {
			position: 'absolute',
			left: 0,
			right: 0,
			bottom: 0,
			height: RIBBON.height * scale,
			alignItems: 'center',
			justifyContent: 'center',
			overflow: 'hidden',
		},
		// Sofia Sans Black 10, text/on-gold.
		ribbonLabel: {
			color: '#2A0A04',
			fontFamily: BONUS_FONT,
			fontSize: RIBBON.fontSize * scale,
			fontWeight: '900',
			letterSpacing: RIBBON.tracking * scale,
			lineHeight: 12 * scale,
		},
		// Same footprint as the coming-soon ribbon, in a dark scrim so the art still shows through.
		timerBand: {
			position: 'absolute',
			left: 0,
			right: 0,
			bottom: 0,
			height: (RIBBON.height + 2) * scale,
			alignItems: 'center',
			justifyContent: 'center',
			backgroundColor: 'rgba(20, 4, 2, 0.75)',
		},
		timerLabel: {
			color: bonusColors.accent,
			fontFamily: BONUS_FONT,
			fontSize: 12 * scale,
			fontWeight: '800',
			letterSpacing: 0.4 * scale,
			lineHeight: 14 * scale,
			fontVariant: ['tabular-nums'],
		},
		play: {
			position: 'absolute',
			left: spec.play.x * scale,
			top: spec.play.y * scale,
		},
	});

export default ArcadeGameCard;
